import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import InteractiveJSONViewer, { VALUE_COLORS, FILTER_COLORS } from '../../components/amazonRawInspect/InteractiveJSONViewer';

const AmazonRawInspect = ({ marginFromSidebar }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const getValueParam = (key, def) => searchParams.get(key) || def;
  
  const defaultEndDate = format(new Date(), 'yyyy-MM-dd');
  const defaultStartDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
  
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() => getValueParam('startDate', defaultStartDate));
  const [endDate, setEndDate] = useState(() => getValueParam('endDate', defaultEndDate));
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [rawData, setRawData] = useState({});
  const [fetchingOrder, setFetchingOrder] = useState(null);
  const [refreshStatus, setRefreshStatus] = useState('ready'); // 'loading' | 'errorred' | 'ready'
  const [refreshError, setRefreshError] = useState(null);
  
  // Two separate path collections:
  // 1. Value paths for summing (with type constraints like [ChargeType=Principal])
  const [selectedValuePaths, setSelectedValuePaths] = useState([]);
  // 2. Array filter paths for filtering orders (generic paths like ShipmentEventList.[].ItemChargeList)
  const [selectedArrayFilters, setSelectedArrayFilters] = useState([]);
  
  const [pathSums, setPathSums] = useState({});
  const [calculatingSums, setCalculatingSums] = useState(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    if (startDate && endDate) {
      fetchOrders();
    }
  }, [startDate, endDate, selectedArrayFilters]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    setSearchParams(params, { replace: true });
  }, [startDate, endDate, setSearchParams]);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const response = await axios.get('/api/v1/amazon-raw-inspect/fetch-all/status', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          }
        });
        const statusData = response.data;
        
        const newStatus = statusData.status || 'ready';
        
        setRefreshStatus(newStatus);
        setRefreshError(statusData.process_error || null);
        
        if (newStatus !== 'loading') {
          stopPolling();
          if (newStatus === 'ready') {
            await fetchOrders();
          }
        }
      } catch (err) {
        console.error('Error polling refresh status:', err);
      }
    }, 4000);
  };

  const checkRefreshStatus = async () => {
    try {
      const response = await axios.get('/api/v1/amazon-raw-inspect/fetch-all/status', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      const statusData = response.data;
      
      setRefreshStatus(statusData.status || 'ready');
      setRefreshError(statusData.process_error || null);
      
      if (statusData.status === 'loading') {
        startPolling();
      }
    } catch (err) {
      console.error('Error checking refresh status:', err);
      setRefreshStatus('ready');
    }
  };

  useEffect(() => {
    checkRefreshStatus();
    return () => stopPolling();
  }, []);

  const fetchOrders = async () => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = { startDate, endDate };
      // Pass array filters to backend for server-side filtering
      if (selectedArrayFilters.length > 0) {
        params.arrayFilters = JSON.stringify(selectedArrayFilters);
      }
      const response = await axios.get('/api/v1/amazon-raw-inspect/orders', {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      setOrders(response.data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.error || err.message);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRawData = async (externalOrderId) => {
    setFetchingOrder(externalOrderId);
    try {
      const response = await axios.get(`/api/v1/amazon-raw-inspect/orders/${externalOrderId}/raw`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      setRawData(prev => ({
        ...prev,
        [externalOrderId]: response.data,
      }));
      setExpandedOrder(externalOrderId);
    } catch (err) {
      console.error('Error fetching raw data:', err);
      if (err.response?.status === 404) {
        // Try to fetch it
        await triggerFetchRawData(externalOrderId);
      } else {
        alert(`Error fetching raw data: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setFetchingOrder(null);
    }
  };

  const triggerFetchRawData = async (externalOrderId) => {
    setFetchingOrder(externalOrderId);
    try {
      await axios.post(`/api/v1/amazon-raw-inspect/orders/${externalOrderId}/fetch`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      // Fetch the raw data after successful fetch
      await fetchRawData(externalOrderId);
      // Refresh orders list to update hasRawData status
      await fetchOrders();
    } catch (err) {
      console.error('Error triggering fetch:', err);
      alert(`Error fetching raw data: ${err.response?.data?.error || err.message}`);
    } finally {
      setFetchingOrder(null);
    }
  };

  const handleFetchAll = async () => {
    if (!startDate || !endDate || refreshStatus === 'loading') return;
    
    setRefreshStatus('loading');
    setRefreshError(null);
    setError(null);
    
    try {
      const response = await axios.post('/api/v1/amazon-raw-inspect/fetch-all', {
        startDate,
        endDate
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.data.started) {
        startPolling();
      }
    } catch (err) {
      console.error('Fetch all failed:', err);
      setRefreshError(err.response?.data?.error || err.message);
      setRefreshStatus('errorred');
      stopPolling();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // Handle value path selection (for summing with type constraints)
  const handleValuePathSelect = (typedPath) => {
    setSelectedValuePaths(prev => {
      const exists = prev.some(p => p.path === typedPath);
      if (exists) {
        return prev.filter(p => p.path !== typedPath);
      } else {
        return [...prev, { path: typedPath }];
      }
    });
  };

  // Handle array filter selection (for filtering orders)
  const handleArrayFilterSelect = (genericPath) => {
    setSelectedArrayFilters(prev => {
      const exists = prev.includes(genericPath);
      if (exists) {
        return prev.filter(p => p !== genericPath);
      } else {
        return [...prev, genericPath];
      }
    });
  };

  const calculateSums = async () => {
    if (selectedValuePaths.length === 0) return;
    
    setCalculatingSums(true);
    try {
      const response = await axios.post('/api/v1/amazon-raw-inspect/sum-paths', {
        startDate,
        endDate,
        paths: selectedValuePaths.map(p => p.path),
        arrayFilters: selectedArrayFilters,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      console.log('Sum response:', response.data);
      setPathSums(response.data.sums || {});
    } catch (err) {
      console.error('Error calculating sums:', err);
      alert(`Error calculating sums: ${err.response?.data?.error || err.message}`);
    } finally {
      setCalculatingSums(false);
    }
  };

  const removeValuePath = (pathToRemove) => {
    setSelectedValuePaths(prev => prev.filter(p => p.path !== pathToRemove));
  };

  const removeArrayFilter = (pathToRemove) => {
    setSelectedArrayFilters(prev => prev.filter(p => p !== pathToRemove));
  };


  const totalSelected = selectedValuePaths.length + selectedArrayFilters.length;

  return (
    <div className={`p-8 ${marginFromSidebar || ''}`}>
      <h1 className="text-2xl font-semibold mb-6">Amazon Financial Data Inspect</h1>

      {/* Date Range Selector */}
      <div className="mb-6 flex flex-wrap gap-3 items-center">
        <div className="flex space-x-2 items-center">
          <label className="text-sm font-medium text-gray-700">From:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-10"
          />
        </div>

        <div className="flex space-x-2 items-center">
          <label className="text-sm font-medium text-gray-700">To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-10"
          />
        </div>

        <div className="flex flex-col items-start">
          <button
            onClick={handleFetchAll}
            disabled={refreshStatus === 'loading' || !startDate || !endDate}
            className={
              refreshStatus === 'loading' || !startDate || !endDate
                ? 'inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                : 'inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            }
          >
            {refreshStatus === 'loading' 
              ? 'Fetching All SP-API Data…' 
              : refreshStatus === 'errorred'
              ? 'Fetch All SP-API Data (Last run failed)'
              : 'Fetch All SP-API Data'}
          </button>
          {refreshStatus === 'errorred' && refreshError && (
            <p className="mt-1 text-sm text-red-600">{refreshError}</p>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Selected Paths Panel - Now with two sections */}
      {totalSelected > 0 && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Selected Paths ({totalSelected})
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedValuePaths([]);
                  setSelectedArrayFilters([]);
                  setPathSums({});
                }}
                className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600"
              >
                Clear All
              </button>
              {selectedValuePaths.length > 0 && (
                <button
                  onClick={calculateSums}
                  disabled={calculatingSums}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {calculatingSums ? 'Calculating...' : 'Calculate Sums'}
                </button>
              )}
            </div>
          </div>
          
          {/* Value Paths Section (for summing) */}
          {selectedValuePaths.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-2">
                <span className="text-purple-600">Σ</span> 
                Value Paths for Summing ({selectedValuePaths.length})
                <span className="text-gray-400 font-normal">- type-constrained (e.g., only ChargeType=Principal)</span>
              </h4>
              <div className="space-y-2">
                {selectedValuePaths.map((pathObj, index) => {
                  const sum = pathSums[pathObj.path];
                  const colors = VALUE_COLORS[index % VALUE_COLORS.length];
                  return (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-2 rounded border-2 ${colors.bg} ${colors.border}`}
                    >
                      <code className="text-xs font-mono text-gray-800 flex-1 break-all">{pathObj.path}</code>
                      {sum !== undefined && (
                        <span className="text-sm font-semibold text-green-700 ml-4 whitespace-nowrap">
                          Sum: ${sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      )}
                      <button
                        onClick={() => removeValuePath(pathObj.path)}
                        className="ml-4 text-red-600 hover:text-red-800 text-sm flex-shrink-0"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Array Filter Paths Section (for filtering) */}
          {selectedArrayFilters.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-2">
                <span className="text-indigo-500">⚡</span>
                Array Filters ({selectedArrayFilters.length})
                <span className="text-gray-400 font-normal">- show only orders with elements in these arrays</span>
              </h4>
              <div className="space-y-2">
                {selectedArrayFilters.map((filterPath, index) => {
                  const colors = FILTER_COLORS[index % FILTER_COLORS.length];
                  return (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-2 rounded border ${colors.bg} ${colors.border}`}
                    >
                      <code className="text-xs font-mono text-gray-800 flex-1 break-all">{filterPath}</code>
                      <button
                        onClick={() => removeArrayFilter(filterPath)}
                        className="ml-4 text-red-600 hover:text-red-800 text-sm flex-shrink-0"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Orders without fetched raw data are still shown (they might match once loaded).
              </p>
            </div>
          )}
        </div>
      )}

      {/* Orders Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-60">
          <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' />
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Raw Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                  <React.Fragment key={order.external_order_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <a
                          href={`https://sellercentral.amazon.com/orders-v3/order/${order.external_order_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {order.external_order_id}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(order.date_of_order)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {order.hasRawData ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Fetched
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Not Fetched
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="relative">
                          <button
                            onClick={() => {
                              if (order.hasRawData) {
                                if (expandedOrder === order.external_order_id) {
                                  setExpandedOrder(null);
                                } else {
                                  fetchRawData(order.external_order_id);
                                }
                              } else {
                                triggerFetchRawData(order.external_order_id);
                              }
                            }}
                            disabled={fetchingOrder === order.external_order_id}
                            className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                          >
                            {fetchingOrder === order.external_order_id ? (
                              'Fetching...'
                            ) : order.hasRawData ? (
                              expandedOrder === order.external_order_id ? 'Hide Raw Data' : 'Show Raw Data'
                            ) : (
                              'Fetch Raw Amazon SP-API'
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedOrder === order.external_order_id && rawData[order.external_order_id] && (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 bg-gray-50">
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                              Raw Amazon SP-API Financial Events Data
                            </h4>
                            <div className="space-y-4">
                              <div>
                                <h5 className="text-xs font-semibold text-gray-600 mb-2">
                                  Financial Events Data:
                                </h5>
                                <div className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-96 border border-gray-300">
                                  {Array.isArray(rawData[order.external_order_id].raw_financials_data) ? (
                                    rawData[order.external_order_id].raw_financials_data.map((eventData, idx) => (
                                      <div key={idx} className="mb-4">
                                        <div className="text-xs font-semibold text-gray-600 mb-2">Event {idx + 1}:</div>
                                        <InteractiveJSONViewer
                                          data={eventData}
                                          onValuePathSelect={handleValuePathSelect}
                                          onArrayFilterSelect={handleArrayFilterSelect}
                                          selectedValuePaths={selectedValuePaths}
                                          selectedArrayFilters={selectedArrayFilters}
                                        />
                                      </div>
                                    ))
                                  ) : (
                                    <InteractiveJSONViewer
                                      data={rawData[order.external_order_id].raw_financials_data}
                                      onValuePathSelect={handleValuePathSelect}
                                      onArrayFilterSelect={handleArrayFilterSelect}
                                      selectedValuePaths={selectedValuePaths}
                                      selectedArrayFilters={selectedArrayFilters}
                                    />
                                  )}
                                </div>
                              </div>
                              {rawData[order.external_order_id].fetched_at && (
                                <div className="text-xs text-gray-500">
                                  Fetched at: {format(new Date(rawData[order.external_order_id].fetched_at), 'MMM d, yyyy HH:mm:ss')}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && !isLoading && (
            <div className="text-center py-8 text-gray-500">
              {selectedArrayFilters.length > 0 
                ? 'No orders match the selected array filters.'
                : 'No Amazon orders found for the selected date range.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AmazonRawInspect;
